/**
 * ProfilePictureManager - Main component for managing user profile pictures.
 *
 * Admin-only feature for managing professional headshots in SharePoint
 * and syncing primary photos to Microsoft Graph.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { ErrorDisplay } from '../../components/errors/ErrorDisplay';
import UserGrid from './components/UserGrid';
import ConsentManagement from './components/ConsentManagement';
import PhotoManagementDialog from './components/PhotoManagementDialog';
import { UserPhotoStatus, UserFilters } from './types';
import { profilePictureApi } from './services/profilePictureApi';
import { useAuth } from '../../contexts/AuthContext';

const ProfilePictureManager: React.FC = () => {
  const { getAccessToken } = useAuth();

  // State
  const [users, setUsers] = useState<UserPhotoStatus[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Filters
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    department: undefined,
    hasPhoto: undefined,
  });
  const [searchInput, setSearchInput] = useState('');

  // Dialog
  const [selectedUser, setSelectedUser] = useState<UserPhotoStatus | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError(new Error('Not authenticated'));
        setLoading(false);
        return;
      }

      const response = await profilePictureApi.getUsersWithPhotoStatus(
        filters,
        {
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
        token
      );

      setUsers(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(new Error(err.response?.data?.detail || err.message || 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, filters, page, pageSize]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleDepartmentChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      department: value === '' ? undefined : value,
    }));
    setPage(1);
  };

  const handleHasPhotoChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      hasPhoto: value === '' ? undefined : value === 'true',
    }));
    setPage(1);
  };

  const handleManagePhotos = (user: UserPhotoStatus) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleUserUpdate = (updatedUser: Partial<UserPhotoStatus> & { user_id: string }) => {
    // Update only the specific user in the array, don't reload entire list
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.user_id === updatedUser.user_id ? { ...user, ...updatedUser } : user
      )
    );
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorDisplay error={error} onRetry={loadUsers} />
      </Container>
    );
  }

  return (
    <Box
      className="main-content"
      sx={{
        height: 'calc(100vh - 64px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box mb={3}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white' }}>
            Profile Picture Management
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Manage professional headshots for all users. Upload photos to SharePoint and sync primary
            photos to Microsoft Graph.
          </Typography>
        </Box>

        <ConsentManagement />

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Search users"
              placeholder="Name, email, or * for all"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 250 }}
            />

            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department || ''}
                onChange={handleDepartmentChange}
                label="Department"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Strategy">Strategy</MenuItem>
                <MenuItem value="Marketing">Marketing</MenuItem>
                <MenuItem value="Engineering">Engineering</MenuItem>
                <MenuItem value="Operations">Operations</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Has Photo</InputLabel>
              <Select
                value={filters.hasPhoto === undefined ? '' : filters.hasPhoto.toString()}
                onChange={handleHasPhotoChange}
                label="Has Photo"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* User Grid */}
        <UserGrid
          users={users}
          total={total}
          page={page}
          pageSize={pageSize}
          loading={loading}
          onPageChange={setPage}
          onManagePhotos={handleManagePhotos}
        />

        {/* Photo Management Dialog */}
        <PhotoManagementDialog
          user={selectedUser}
          open={dialogOpen}
          onClose={handleDialogClose}
          onUserUpdate={handleUserUpdate}
        />
      </Container>
    </Box>
  );
};

export default ProfilePictureManager;
